import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getChain } from '../api.js';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://kindkudos.app';

export default function Chain() {
  const { rootId } = useParams();
  const [chain, setChain] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getChain(rootId)
      .then((data) => {
        if (!cancelled) setChain(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load this chain.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rootId]);

  if (loading) {
    return <p className="mt-10 text-center text-navy-700">Loading&hellip;</p>;
  }

  if (error || !chain) {
    return (
      <section className="mt-10 text-center">
        <div className="card max-w-xl mx-auto">
          <h1 className="text-2xl font-display font-bold mb-2">
            We couldn&rsquo;t load this chain.
          </h1>
          <p className="text-navy-700 mb-4">{error || 'It may have been removed.'}</p>
          <Link to="/" className="btn-primary no-underline">
            Send one of your own
          </Link>
        </div>
      </section>
    );
  }

  const total = chain.totalCount || 0;
  const depth = chain.maxDepth || 0;
  const shareUrl = `${SITE_URL}/chain/${rootId}`;
  const shareText = `${total} ${total === 1 ? 'person has' : 'people have'} been part of a kindness chain on KindKudos. See it grow:`;

  async function copyShareLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard.');
    } catch {
      window.prompt('Copy this link:', shareUrl);
    }
  }

  return (
    <section className="mt-10 space-y-6">
      <div className="card max-w-xl mx-auto text-center">
        <div className="text-5xl mb-4">🌱</div>
        <h1 className="text-3xl font-display font-bold text-navy mb-2">
          A kindness chain
        </h1>
        <p className="text-navy-700 mb-4">
          {total} {total === 1 ? 'kudo' : 'kudos'} so far
          {depth > 0 ? `, across ${depth} hand-off${depth === 1 ? '' : 's'}` : ''}.
        </p>
        <p className="text-sm text-navy-500 mb-5">
          We show counts and shape only &mdash; never names or messages.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <button onClick={copyShareLink} className="btn-primary">
            Copy share link
          </button>
          <a
            className="btn-accent no-underline"
            href={`mailto:?subject=${encodeURIComponent('A growing kindness chain')}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`}
          >
            Share via email
          </a>
        </div>
      </div>

      <p className="text-center text-sm text-navy-500">
        <Link to="/" className="text-teal-700">Start one of your own</Link>
      </p>
    </section>
  );
}
